import { reactive, readonly } from 'vue';
import { IDailyWordTrackingService, IUserFsrsCardService, Language } from '@lingvo-news/contracts';
import { getCurrentUserStore } from '../services';
import { serviceContainer } from '../service-container';

interface WordStatsState {
	totalWords: number;
	wordsToRepeat: number;
	dailyWordsLeft: number;
	dailyBookmarked: number;
	dailyLearned: number;
	isLoading: boolean;
	lastUpdated: Date | null;
	nativeLanguage: Language | null;
	learnedLanguage: Language | null;
}

const state = reactive<WordStatsState>({
	totalWords: 0,
	wordsToRepeat: 0,
	dailyWordsLeft: 10,
	dailyBookmarked: 0,
	dailyLearned: 0,
	isLoading: false,
	lastUpdated: null,
	nativeLanguage: null,
	learnedLanguage: null,
});

const currentUserStore = getCurrentUserStore();

async function resolveUserId(): Promise<string | null> {
	await currentUserStore.initialize();
	const userId = currentUserStore.getCurrentUserId();
	return userId ?? null;
}

let fakeWordCount = 0;
const STATS_CACHE_TTL = 30_000; // 30 seconds
let activeRequest: Promise<void> | null = null;

function updateLanguages(nativeLanguage: Language, learnedLanguage: Language): void {
	state.nativeLanguage = nativeLanguage;
	state.learnedLanguage = learnedLanguage;
}

function isCacheValid(nativeLanguage?: Language, learnedLanguage?: Language): boolean {
	if (!state.lastUpdated) return false;
	const languageChanged =
		(nativeLanguage && nativeLanguage !== state.nativeLanguage) ||
		(learnedLanguage && learnedLanguage !== state.learnedLanguage);
	if (languageChanged) return false;
	return Date.now() - state.lastUpdated.getTime() < STATS_CACHE_TTL;
}

async function loadStats(nativeLanguage?: Language, learnedLanguage?: Language): Promise<void> {
	if (isCacheValid(nativeLanguage, learnedLanguage)) return;
	await fetchStats(nativeLanguage, learnedLanguage);
}

async function fetchStats(nativeLanguage?: Language, learnedLanguage?: Language): Promise<void> {
	if (activeRequest) return activeRequest;

	activeRequest = (async () => {
		try {
			state.isLoading = true;
			const userId = await resolveUserId();
			if (!userId) {
				reset();
				return;
			}
			if (nativeLanguage && learnedLanguage)
				updateLanguages(nativeLanguage, learnedLanguage);

			if (!state.nativeLanguage || !state.learnedLanguage) {
				reset();
				return;
			}

			const wordsService = serviceContainer.get(IUserFsrsCardService);
			const dailyWordTrackingService = serviceContainer.get(IDailyWordTrackingService);
			const [stats, dailyStats] = await Promise.all([
				wordsService.getWordStats({
					userId,
					nativeLanguage: state.nativeLanguage,
					learnedLanguage: state.learnedLanguage,
				}),
				dailyWordTrackingService.getDailyStats({
					userId,
					nativeLanguage: state.nativeLanguage,
					learnedLanguage: state.learnedLanguage,
					userTimeZoneOffset: new Date().getTimezoneOffset(),
				}),
			]);

			state.totalWords = stats.totalWords;
			state.wordsToRepeat = stats.wordsToRepeat;
			state.dailyBookmarked = dailyStats.learned.length;
			state.dailyLearned = dailyStats.validated.length;
			state.dailyWordsLeft = Math.max(0, dailyStats.needToLearn);
			state.lastUpdated = new Date();
		} catch (error) {
			console.error('Error loading word stats:', error);
		} finally {
			state.isLoading = false;
			activeRequest = null;
		}
	})();

	return activeRequest;
}

async function refreshStats(nativeLanguage?: Language, learnedLanguage?: Language): Promise<void> {
	await fetchStats(nativeLanguage, learnedLanguage);
}

function reset(): void {
	state.totalWords = fakeWordCount;
	state.wordsToRepeat = 0;
	state.dailyWordsLeft = 10;
	state.dailyBookmarked = 0;
	state.dailyLearned = 0;
	state.isLoading = false;
	state.lastUpdated = null;
}

function incrementTotalWords(count: number = 1): void {
	if (count <= 0) {
		return;
	}
	fakeWordCount += count;
	state.totalWords = fakeWordCount;
	state.lastUpdated = new Date();
}

let usedWords = new Set<string>();

function incrementTotalWordsForWord(word: string): void {
	if (state.totalWords || 0!==usedWords.size)
		return;

	if (usedWords.has(word)) {
		return;
	}
	usedWords.add(word);
	state.totalWords += 1;
	state.lastUpdated = new Date();
}

function decrementTotalWords(count: number = 1): void {
	if (count <= 0) {
		return;
	}

	state.totalWords = Math.max(0, state.totalWords - count);
	state.lastUpdated = new Date();
}

function setWordsToRepeatCount(count: number): void {
	state.wordsToRepeat = Math.max(0, count);
	state.lastUpdated = new Date();
}

export function useWordStatsStore() {
	return {
		state: readonly(state),
		loadStats,
		refreshStats,
		reset,
		incrementTotalWords,
		incrementTotalWordsForWord,
		setWordsToRepeatCount
	};
}


